/**
 * TranslationToggle · v2.7.45+E7 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Spec §5 — translate the currently-open message into one of 5 target
 * languages. Tags + URLs + tracking pixels are protected on the backend.
 *
 * Client-side LRU cache (Map keyed by `${lang}:${hash}`) so flipping
 * back and forth between source/translation never re-hits the backend.
 * View Original toggle restores the unmodified body instantly.
 */
import { useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Loader2, Languages, RotateCcw } from "lucide-react";

const LANGS = [
  { code: "en", label: "English"  },
  { code: "es", label: "Español"  },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch"  },
  { code: "zh", label: "中文"     },
];

// Tiny local LRU — keyed by sha-ish of content+lang. We avoid actually
// hashing (FNV / sha256 in JS is fine but unnecessary for this scale —
// 50 cached strings per message viewer max).
const CACHE = new Map();
const CACHE_LIMIT = 200;
function cacheGet(k) { return CACHE.get(k); }
function cacheSet(k, v) {
  if (CACHE.size >= CACHE_LIMIT) {
    const first = CACHE.keys().next().value;
    CACHE.delete(first);
  }
  CACHE.set(k, v);
}

export default function TranslationToggle({ original, isHtml = false, onTranslated }) {
  const [busy, setBusy] = useState(false);
  const [currentLang, setCurrentLang] = useState("en");
  const [translated, setTranslated] = useState(null);

  const cacheKey = (lang) => `${lang}:${isHtml ? "h" : "t"}:${original?.length || 0}:${(original || "").slice(0, 64)}`;

  const translate = async (lang) => {
    if (lang === "en") {
      setCurrentLang("en");
      setTranslated(null);
      onTranslated?.(null);
      return;
    }
    const k = cacheKey(lang);
    const hit = cacheGet(k);
    if (hit) {
      setCurrentLang(lang);
      setTranslated(hit);
      onTranslated?.(hit);
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/translation/translate", {
        content: original || "",
        target_lang: lang,
        is_html: isHtml,
      });
      cacheSet(k, data.translated);
      setCurrentLang(lang);
      setTranslated(data.translated);
      onTranslated?.(data.translated);
    } catch (e) {
      // toast handled by parent if it wants
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="translation-toggle">
      <Languages className="h-3 w-3 text-slate-500"/>
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mr-1">Translate</span>
      {LANGS.map((l) => (
        <Button
          key={l.code}
          size="sm" variant={currentLang === l.code ? "default" : "outline"}
          className={`h-6 px-2 text-[10px] font-bold ${currentLang === l.code ? "bg-sky-600 hover:bg-sky-500 text-white" : ""}`}
          onClick={() => translate(l.code)}
          disabled={busy}
          data-testid={`translate-${l.code}`}
        >
          {busy && currentLang !== l.code ? <Loader2 className="h-3 w-3 animate-spin"/> : l.label}
        </Button>
      ))}
      {translated && (
        <Button
          size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1 text-amber-600 dark:text-amber-300"
          onClick={() => translate("en")}
          data-testid="translate-view-original"
          title="Restore original text"
        >
          <RotateCcw className="h-3 w-3"/>View Original
        </Button>
      )}
    </div>
  );
}
